package org.chiangmeng.qfast.features.auth

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import org.chiangmeng.qfast.core.ui.staff.StaffScreen
import org.chiangmeng.qfast.core.ui.theme.QFastTheme
import org.chiangmeng.qfast.features.auth.AppleSignInButton

class AuthScreen : Screen {
    @Composable
    override fun Content() {
        val viewModel = koinScreenModel<AuthViewModel>()
        val navigator = LocalNavigator.currentOrThrow
        val uiState by viewModel.uiState.collectAsState()

        var isRegisterMode by remember { mutableStateOf(false) }
        var email by remember { mutableStateOf("") }
        var password by remember { mutableStateOf("") }
        var shopName by remember { mutableStateOf("") }

        LaunchedEffect(Unit) {
            viewModel.checkInitialAuth()
        }

        LaunchedEffect(uiState) {
            if (uiState is AuthUiState.Authenticated) {
                val shopId = (uiState as AuthUiState.Authenticated).shopId
                navigator.replaceAll(StaffScreen(shopId = shopId))
            }
        }

        var newSocialShopName by remember { mutableStateOf("") }
        val needShopNameState = uiState as? AuthUiState.NeedShopName
        if (needShopNameState != null) {
            AlertDialog(
                onDismissRequest = { viewModel.cancelSocialSignUp() },
                title = { Text("ระบุชื่อร้านค้าของคุณ") },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text(
                            text = "กรุณาระบุชื่อร้านค้าเพื่อเริ่มต้นใช้งาน QFast",
                            fontSize = 14.sp
                        )
                        OutlinedTextField(
                            value = newSocialShopName,
                            onValueChange = { newSocialShopName = it },
                            label = { Text("ชื่อร้านค้า") },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true
                        )
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            if (newSocialShopName.isNotBlank()) {
                                viewModel.completeSocialSignUp(needShopNameState.uid, newSocialShopName)
                            }
                        }
                    ) {
                        Text("สร้างร้านค้า")
                    }
                },
                dismissButton = {
                    TextButton(onClick = { viewModel.cancelSocialSignUp() }) {
                        Text("ยกเลิก")
                    }
                }
            )
        }

        QFastTheme {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color(0xFF0F172A)),
                contentAlignment = Alignment.Center
            ) {
                Card(
                    modifier = Modifier
                        .widthIn(max = 440.dp)
                        .fillMaxWidth()
                        .padding(24.dp),
                    shape = RoundedCornerShape(16.dp),
                    colors = CardDefaults.cardColors(containerColor = Color(0xFF1E293B))
                ) {
                    Column(
                        modifier = Modifier.padding(28.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Text(
                            text = if (isRegisterMode) "สร้างร้านค้าใหม่" else "เข้าสู่ระบบ QFast",
                            fontSize = 22.sp,
                            fontWeight = FontWeight.Bold,
                            color = Color.White
                        )

                        if (isRegisterMode) {
                            OutlinedTextField(
                                value = shopName,
                                onValueChange = { shopName = it },
                                label = { Text("ชื่อร้านค้าของคุณ") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                        }

                        OutlinedTextField(
                            value = email,
                            onValueChange = { email = it },
                            label = { Text("อีเมล") },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true
                        )

                        OutlinedTextField(
                            value = password,
                            onValueChange = { password = it },
                            label = { Text("รหัสผ่าน (อย่างน้อย 6 ตัวอักษร)") },
                            visualTransformation = PasswordVisualTransformation(),
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true
                        )

                        if (uiState is AuthUiState.Error) {
                            Text(
                                text = (uiState as AuthUiState.Error).message,
                                color = MaterialTheme.colorScheme.error,
                                fontSize = 14.sp
                            )
                        }

                        Button(
                            onClick = {
                                if (isRegisterMode) {
                                    viewModel.signUpAndCreateShop(email, password, shopName)
                                } else {
                                    viewModel.signIn(email, password)
                                }
                            },
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(48.dp),
                            enabled = uiState !is AuthUiState.Loading,
                            shape = RoundedCornerShape(8.dp)
                        ) {
                            if (uiState is AuthUiState.Loading) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(24.dp),
                                    color = Color.White,
                                    strokeWidth = 2.dp
                                )
                            } else {
                                Text(if (isRegisterMode) "สมัครสมาชิกและสร้างร้าน" else "เข้าสู่ระบบ")
                            }
                        }

                        TextButton(
                            onClick = { isRegisterMode = !isRegisterMode }
                        ) {
                            Text(
                                text = if (isRegisterMode) "มีบัญชีอยู่แล้ว? เข้าสู่ระบบ" else "ยังไม่มีบัญชีร้านค้า? สมัครใช้งาน",
                                color = Color(0xFF38BDF8)
                            )
                        }

                        HorizontalDivider(
                            modifier = Modifier.padding(vertical = 4.dp),
                            color = Color(0xFF334155)
                        )
                        GoogleSignInButton(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(48.dp),
                            enabled = uiState !is AuthUiState.Loading,
                            onTokenReceived = { idToken, accessToken ->
                                viewModel.signInWithGoogle(idToken, accessToken)
                            }
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        AppleSignInButton(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(48.dp),
                            enabled = uiState !is AuthUiState.Loading,
                            onTokenReceived = { idToken, rawNonce ->
                                viewModel.signInWithApple(idToken, rawNonce)
                            }
                        )
                    }
                }
            }
        }
    }
}
